/* ================================================================== *
 *  Music Explorer Challenge — the Rhythm community's daily quiz.       *
 *  Source of truth: client "Music Explorer Challenge" workbook        *
 *  (Overview / Question Bank / Reward System & Tiers / Redemption).    *
 *  20 questions · 4 categories × 5 · client-side scoring, no backend.  *
 * ================================================================== */

export type QuizQuestion = {
  id: number
  category: string
  q: string
  options: [string, string, string, string]
  /* index (0–3) of the correct option */
  correct: 0 | 1 | 2 | 3
}

import type { IconName } from './content'

export const quizCategories = [
  { key: 'Bollywood', label: 'Bollywood', icon: 'star' as IconName },
  { key: 'Indian Classical', label: 'Indian Classical', icon: 'mic' as IconName },
  { key: 'Folk & Indian Heritage', label: 'Folk & Heritage', icon: 'leaf' as IconName },
  { key: 'Music Knowledge & Learning', label: 'Music Knowledge', icon: 'sparkle' as IconName },
] as const

/* Coin mechanics — from the "Reward System & Tiers" tab. */
export const coinRules = {
  perCorrect: 10, // +10 per correct answer
  streakBonus: 25, // +25 for every 5 correct in a row
  streakEvery: 5,
  perfectBonus: 100, // +100 for a clean 20/20
  completionBonus: 20, // +20 for finishing a session
}

/* Achievement tiers — from the "Reward System & Tiers" tab.
   `min` is the minimum correct-answer count for the rank. */
export const quizRanks = [
  { min: 20, icon: 'star' as IconName, name: 'Music Maestro', perk: 'Gold Badge + Profile Frame' },
  { min: 18, icon: 'sparkle' as IconName, name: 'Virtuoso', perk: 'Silver Badge' },
  { min: 15, icon: 'mic' as IconName, name: 'Rising Musician', perk: 'Standard Completion' },
  { min: 10, icon: 'compass' as IconName, name: 'Music Explorer', perk: 'Standard Completion' },
  { min: 0, icon: 'leaf' as IconName, name: 'Beginner', perk: 'Participation Perk' },
] as const

export function rankFor(score: number) {
  return quizRanks.find((r) => score >= r.min) ?? quizRanks[quizRanks.length - 1]
}

/* Coin redemption store — from the "Redemption & Themes" tab. */
export const redemptionStore = [
  { icon: 'mic' as IconName, title: 'Free trial vocal class', note: 'Live interactive singing' },
  { icon: 'star' as IconName, title: 'Instrument workshop pass', note: 'Guitar, piano & keyboard intros' },
  { icon: 'sparkle' as IconName, title: 'Premium lesson unlocks', note: 'Exclusive course modules' },
  { icon: 'shield' as IconName, title: 'Course discounts (10–20% off)', note: 'For casual quizzers going pro' },
  { icon: 'users' as IconName, title: 'Live mentor Q&A sessions', note: 'Time with expert instructors' },
  { icon: 'heart' as IconName, title: 'Exclusive profile badges', note: 'Community prestige' },
]

/* Rotating weekly themes — flavour shown on the intro screen. */
export const weeklyThemes = [
  'Bollywood Legends', 'Indian Classical Challenge', 'Playback Singer Special',
  'Instruments Around the World', 'Folk Music of India', 'Music Theory Made Easy',
  'Guess the Music Composer', 'Women Who Shaped Indian Music',
  'Oscar & Grammy Winners from India', 'Music Through the Decades',
]

export const quizQuestions: QuizQuestion[] = [
  { id: 1, category: 'Bollywood', q: 'Who sang the song “Kesariya” from Brahmāstra?', options: ['Sonu Nigam', 'Arijit Singh', 'Jubin Nautiyal', 'Shaan'], correct: 1 },
  { id: 2, category: 'Bollywood', q: 'Which music director composed the soundtrack of Rockstar?', options: ['Pritam', 'A.R. Rahman', 'Vishal-Shekhar', 'Amit Trivedi'], correct: 1 },
  { id: 3, category: 'Bollywood', q: 'Which movie features the song “Kal Ho Naa Ho”?', options: ['Veer-Zaara', 'Kal Ho Naa Ho', 'Kabhi Khushi Kabhie Gham', 'Salaam Namaste'], correct: 1 },
  { id: 4, category: 'Bollywood', q: 'Which playback singer is known as the “Melody Queen” of modern Bollywood?', options: ['Neha Kakkar', 'Shreya Ghoshal', 'Sunidhi Chauhan', 'Alka Yagnik'], correct: 1 },
  { id: 5, category: 'Bollywood', q: 'Which singer won the National Award for the song “Bolo Na” from Chittagong?', options: ['Arijit Singh', 'Shankar Mahadevan', 'Sonu Nigam', 'Hariharan'], correct: 1 },
  { id: 6, category: 'Indian Classical', q: 'How many basic swaras are there in Indian classical music?', options: ['5', '6', '7', '8'], correct: 2 },
  { id: 7, category: 'Indian Classical', q: 'What is the first swara in Indian classical music?', options: ['Re', 'Sa', 'Ga', 'Ma'], correct: 1 },
  { id: 8, category: 'Indian Classical', q: 'Who is famously known as the “Sitar Maestro”?', options: ['Zakir Hussain', 'Pandit Ravi Shankar', 'Hariprasad Chaurasia', 'Shivkumar Sharma'], correct: 1 },
  { id: 9, category: 'Indian Classical', q: 'Which instrument is Ustad Zakir Hussain famous for playing?', options: ['Sitar', 'Tabla', 'Santoor', 'Sarod'], correct: 1 },
  { id: 10, category: 'Indian Classical', q: 'A “Taal” refers to:', options: ['A musical instrument', 'Rhythm cycle', 'A song', 'A singer'], correct: 1 },
  { id: 11, category: 'Folk & Indian Heritage', q: 'Bihu music belongs to which state?', options: ['Rajasthan', 'Assam', 'Gujarat', 'Punjab'], correct: 1 },
  { id: 12, category: 'Folk & Indian Heritage', q: 'Lavani is a traditional music and dance form from:', options: ['Maharashtra', 'Kerala', 'Odisha', 'Tamil Nadu'], correct: 0 },
  { id: 13, category: 'Folk & Indian Heritage', q: 'Which state is famous for Bhangra music?', options: ['Punjab', 'Haryana', 'Rajasthan', 'Uttar Pradesh'], correct: 0 },
  { id: 14, category: 'Folk & Indian Heritage', q: 'Baul music is traditionally associated with:', options: ['West Bengal', 'Gujarat', 'Bihar', 'Himachal Pradesh'], correct: 0 },
  { id: 15, category: 'Folk & Indian Heritage', q: 'Which instrument is traditionally associated with Rajasthani folk music?', options: ['Ravanhatta', 'Piano', 'Guitar', 'Violin'], correct: 0 },
  { id: 16, category: 'Music Knowledge & Learning', q: 'Which of these instruments belongs to the percussion family?', options: ['Guitar', 'Tabla', 'Violin', 'Flute'], correct: 1 },
  { id: 17, category: 'Music Knowledge & Learning', q: 'Which instrument has 88 keys?', options: ['Harmonium', 'Piano', 'Keyboard', 'Accordion'], correct: 1 },
  { id: 18, category: 'Music Knowledge & Learning', q: 'Which of these is a string instrument?', options: ['Tabla', 'Guitar', 'Flute', 'Harmonium'], correct: 1 },
  { id: 19, category: 'Music Knowledge & Learning', q: 'Which habit helps beginners improve in music the most?', options: ['Practicing a little every day', 'Practicing only on weekends', 'Watching performances only', 'Buying expensive equipment'], correct: 0 },
  { id: 20, category: 'Music Knowledge & Learning', q: 'Which famous Indian musician is known as the “Flute Maestro”?', options: ['Hariprasad Chaurasia', 'Pandit Ravi Shankar', 'Zakir Hussain', 'Shivkumar Sharma'], correct: 0 },
]
